/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ChoiceGroup,
    IChoiceGroupOption,
    IChoiceGroupOptionProps,
    IChoiceGroupOptionStyles,
    IChoiceGroupProps,
    IChoiceGroupStyles
} from '@fluentui/react/lib/ChoiceGroup';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { prettyPrintEntity } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';

const choiceGroupStyles = classNamesFunction<IChoiceGroupProps, IChoiceGroupStyles>()({
    flexContainer: { display: 'flex', alignItems: 'flex-start' }
});
const optionStyles = classNamesFunction<IChoiceGroupOptionProps, IChoiceGroupOptionStyles>()({
    root: { marginTop: '0px', paddingRight: '24px' }
});

type EntityTypeSelectionProps = {
    /**
     * List of entity models which is allowed
     * to be created.
     */
    allowedModelTypes: readonly ModelType[];

    /**
     * Model type option being selected
     */
    selectedModelType: ModelType;

    /**
     * Callback to set model type option being selected
     */
    onChangeModelType: (choice: ModelType) => void;
};

const messages = defineMessages({ typeLabel: { id: 'entities.entityTypeSelection.typeLabel', defaultMessage: 'Type:' } });

export const EntityTypeSelection = (props: EntityTypeSelectionProps) => {
    const { selectedModelType, allowedModelTypes, onChangeModelType } = props;
    const { localize } = useLocalization();

    const entityTypeOptions: IChoiceGroupOption[] = allowedModelTypes.map(modelType => ({
        key: `${modelType}`,
        styles: optionStyles,
        text: prettyPrintEntity(modelType)
    }));

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16, padding: '0 0 8px 0' }}>
            <Label id="entityTypeChoiceGroupLabel">{localize(messages.typeLabel)}</Label>

            <ChoiceGroup
                styles={choiceGroupStyles}
                options={entityTypeOptions}
                selectedKey={`${selectedModelType}`}
                ariaLabelledBy="entityTypeChoiceGroupLabel"
                onChange={(_, option) => onChangeModelType(parseInt(option.key, 10))}
            />
        </Stack>
    );
};
